import type { Snapshot } from 'recoil';
import { DefaultValue, selector } from 'recoil';
import type { BenchmarkConfig, Subject, SubjectWithOptionalFee } from './types';
import type {
  AnalysisPeriod,
  AnalysisSubjectTypeEnum,
  AnalysisView,
  AnalysisViewBenchmarkSettings,
  AnalysisViewDateRange,
  AnalysisViewSubject,
  AnalysisViewSubjectGroup,
  CustomizedBlock,
  LinkedItem,
} from 'venn-api';
import {
  type BenchmarkInputId,
  type DateRangeInputId,
  inaccessibleSubjectsState,
  type INPUT_MANAGEMENT_VERSION,
  type SubjectInputId,
  analysisViewTypeState,
  benchmarkInputIsRelative,
  benchmarkInputName,
  benchmarkInputs,
  benchmarkInputSubject,
  benchmarkInputType,
  blockBenchmarkConfig,
  blockSettings,
  blockSubjects,
  customizedBlock,
  customViewOptions,
  dateRangeInputConsistencyState,
  dateRangeInputDateRangeState,
  dateRangeInputNameState,
  dateRangeInputsState,
  globalBenchmarkConfig,
  globalBenchmarkRelative,
  globalBenchmarkSubject,
  globalBenchmarkType,
  globalCustomViewOptions,
  globalSubjectFees,
  globalSubjects,
  isLinked,
  overriddenBlockDateRange,
  overriddenBlockSubjects,
  overriddenGlobalDateRange,
  studioPrintOrientationType,
  subjectInputGroupName,
  subjectInputGroups,
  subjectInputGroupSubjects,
  unlinkedBenchmarkSubject,
  unlinkedSubjectFees,
  viewInputManagementVersion,
} from './configuration';
import type { DateRange, RangeType } from 'venn-ui-kit';
import { compact, omit } from 'lodash';
import { clearHasUnsavedChanges } from '../effects/unsavedChangesEffect';
import type { CustomBlockTypeEnum } from 'venn-utils';
import { assertExhaustive } from 'venn-utils';

export const currentAnalysisView = selector<AnalysisView | undefined>({
  key: 'currentAnalysisView',
  // TODO: Implement get when all fields of analysis view are mapped to state atoms
  get: () => undefined,
  set: ({ set }, view) => {
    if (view instanceof DefaultValue || view === undefined) {
      return;
    }
    // TODO(will, collin): imv2 P0: support serialization/deserialization (persistent storage) of inputs
    const inputManagementVersion = view.version as INPUT_MANAGEMENT_VERSION;
    set(viewInputManagementVersion, inputManagementVersion);

    view.printOrientationType && set(studioPrintOrientationType, view.printOrientationType);

    set(inaccessibleSubjectsState, getInaccessibleSubjects(view));

    if (inputManagementVersion === 1) {
      set(globalSubjects, convertSubjects(view.subjects));
      set(overriddenGlobalDateRange, convertAnalysisPeriodToDateRange(view.analysisPeriod));
      set(globalBenchmarkSubject, getBenchmark(view));
      set(globalSubjectFees, extractFees(view));
    } else if (inputManagementVersion === 2) {
      set(subjectInputGroups, view.subjectGroups?.map((g) => g.id as SubjectInputId) ?? []);
      view.subjectGroups?.forEach(({ id, subjects, label }) => {
        set(
          subjectInputGroupSubjects(id as SubjectInputId),
          subjects
            .filter((subject) => !subject.subjectInaccessible)
            .map((s) => {
              const feesMapping = s.feesMapping ?? {};
              if (s.isPrivate && s.subjectType === 'PORTFOLIO') {
                return { privatePortfolioId: s.id, feesMapping };
              }
              if (s.isPrivate && s.subjectType === 'INVESTMENT') {
                return { privateFundId: s.id, feesMapping };
              }
              if (s.subjectType === 'PORTFOLIO') {
                return { portfolioId: Number(s.id), feesMapping };
              }
              return { fundId: s.id, feesMapping };
            }),
        );
        set(subjectInputGroupName(id as SubjectInputId), label);
      });
      set(dateRangeInputsState, view.dateRanges?.map((d) => d.id as DateRangeInputId) ?? []);
      view.dateRanges?.forEach(({ id, period, label, resolution }) => {
        set(dateRangeInputNameState(id as DateRangeInputId), label);
        set(dateRangeInputDateRangeState(id as DateRangeInputId), convertAnalysisPeriodToDateRange(period));
        set(dateRangeInputConsistencyState(id as DateRangeInputId), resolution);
      });

      set(benchmarkInputs, view.benchmarkSettings?.map((s) => s.id as BenchmarkInputId) ?? []);
      view.benchmarkSettings?.forEach(({ id, label, relative, subject, type }) => {
        set(benchmarkInputName(id as BenchmarkInputId), label);
        set(benchmarkInputIsRelative(id as BenchmarkInputId), relative);
        subject &&
          !subject.subjectInaccessible &&
          set(benchmarkInputSubject(id as BenchmarkInputId), analysisSubjectToSubject(subject));
        set(benchmarkInputType(id as BenchmarkInputId), type);
      });
    } else {
      throw assertExhaustive(inputManagementVersion);
    }

    set(globalCustomViewOptions, view.customViewOptions);

    if (view.customizedBlock) {
      // TODO(will, collin, hesham): imv2 P0 don't set global benchmark info once benchmark recoil slice is implemented
      set(globalBenchmarkRelative, view.customizedBlock.relativeToBenchmark);
      view.customizedBlock.customBenchmarkType && set(globalBenchmarkType, view.customizedBlock.customBenchmarkType);
    }

    view.customizedViews?.forEach((customView) => {
      if (!customView.refId) {
        return;
      }

      if (customView.customizedBlock) {
        set(customizedBlock(customView.refId), customView.customizedBlock);
      }
      set(customViewOptions(customView.refId), customView.customViewOptions);

      if (inputManagementVersion === 1) {
        const linked = !!customView.linkedItems?.length;
        set(isLinked(customView.refId), linked);
        if (!linked) {
          // Beware: if isLinked is used in the below sets, it may return the default value of isLinked rather than the current value of
          // linked due to a problem with read-write consistency / out-of-order execution
          set(overriddenBlockSubjects(customView.refId), convertSubjects(customView.subjects));
          set(overriddenBlockDateRange(customView.refId), convertAnalysisPeriodToDateRange(customView.analysisPeriod));
          set(unlinkedBenchmarkSubject(customView.refId), getBenchmark(customView));
          set(unlinkedSubjectFees(customView.refId), extractFees(customView));
        }
      } else if (inputManagementVersion === 2) {
        // TODO
      } else {
        throw assertExhaustive(inputManagementVersion);
      }
    });

    view.analysisViewType && set(analysisViewTypeState, view.analysisViewType);

    // We just loaded an entirely new view so it doesn't have unsaved changes.
    // The fact we're using setTimeout is sort of a hack to get around the semi-asynchronous nature of recoil sets.
    // There might be a better way to do this.
    setTimeout(clearHasUnsavedChanges, 0);
  },
});

export const combineAnalysisViewWithSnapshot = async (
  view: Partial<AnalysisView>,
  snapshot: Snapshot,
): Promise<Partial<AnalysisView>> => {
  let customizedViews: AnalysisView[] = [];
  const version = await snapshot.getPromise(viewInputManagementVersion);
  const globalFees = await snapshot.getPromise(globalSubjectFees);

  if (view.customizedViews) {
    customizedViews = await Promise.all(
      view.customizedViews.map(async (customView) => {
        if (!customView.refId) {
          return customView;
        }

        const linked = await snapshot.getPromise(isLinked(customView.refId));
        const linkedItems = linked ? getStandardLinkedFields(view.refId ?? '') : [];
        const settings = await snapshot.getPromise(blockSettings(customView.refId));
        const block = filterBlockSettings(
          settings.customBlockType,
          await snapshot.getPromise(customizedBlock(customView.refId)),
        );
        const viewOptions = await snapshot.getPromise(customViewOptions(customView.refId));
        const subjectGroup = await snapshot.getPromise(blockSubjects(customView.refId));
        const benchmark = await snapshot.getPromise(blockBenchmarkConfig(customView.refId));
        const analysisPeriod = convertDateRangeToAnalysisPeriod(
          await snapshot.getPromise(overriddenBlockDateRange(customView.refId)),
        );
        const unlinkedFees = await snapshot.getPromise(unlinkedSubjectFees(customView.refId));
        const subjects = getSubjects(subjectGroup, benchmark, linked ? globalFees : unlinkedFees);
        return {
          ...customView,
          analysisPeriod,
          customizedBlock: block,
          linkedItems,
          customViewOptions: {
            ...customView.customViewOptions,
            ...viewOptions,
          },
          subjects,
          version,
        };
      }),
    );
  }

  const globalSubjectGroup = await snapshot.getPromise(globalSubjects);
  const globalBenchmark = await snapshot.getPromise(globalBenchmarkConfig);
  const subjects = getSubjects(globalSubjectGroup, globalBenchmark, globalFees);
  const viewOptions = await snapshot.getPromise(globalCustomViewOptions);
  const subjectGroups = await getSubjectGroups(snapshot);
  const dateRanges = await getDateRanges(snapshot);
  const benchmarkSettings = await getBenchmarkSettings(snapshot);
  const analysisPeriod = convertDateRangeToAnalysisPeriod(await snapshot.getPromise(overriddenGlobalDateRange));
  const printOrientationType = await snapshot.getPromise(studioPrintOrientationType);

  return {
    ...view,
    analysisPeriod,
    customizedViews,
    subjects,
    printOrientationType,
    benchmarkSettings,
    subjectGroups,
    dateRanges,
    version,
    customizedBlock: {
      relativeToBenchmark: globalBenchmark.relative,
      customBenchmarkType: globalBenchmark.type,
      contributionToPercentage: false,
      selectedFactors: [],
      selectedMetrics: [],
      linkedBenchmarkSettings: '',
      linkedDateRange: '',
      linkedSubjectGroups: [],
    },
    customViewOptions: {
      ...view.customViewOptions,
      ...viewOptions,
    },
  };
};

type OptionalFields = 'confidenceLevels' | 'confidenceLevelsVisibility';

const getSubjectGroups = async (snapshot: Snapshot): Promise<AnalysisViewSubjectGroup[]> => {
  const ids = await snapshot.getPromise(subjectInputGroups);
  return Promise.all(
    ids.map(async (id) => {
      const label = await snapshot.getPromise(subjectInputGroupName(id));
      const temp = await snapshot.getPromise(subjectInputGroupSubjects(id));
      const subjects: AnalysisViewSubject[] = temp.map((subject) => ({
        comparisonType: 'COMPARISON' as const,
        id: subject.privateFundId ?? subject.privatePortfolioId ?? subject.fundId ?? String(subject.portfolioId),
        subjectType: getAnalysisSubjectType(subject),
        feesMapping: subject.feesMapping,
        isPrivate: !!subject.privateFundId || !!subject.privatePortfolioId,
      }));
      return {
        id,
        label,
        subjects,
      };
    }),
  );
};

const getDateRanges = async (snapshot: Snapshot): Promise<AnalysisViewDateRange[]> => {
  const ids = await snapshot.getPromise(dateRangeInputsState);
  return Promise.all(
    ids.map(async (id) => {
      const label = await snapshot.getPromise(dateRangeInputNameState(id));
      const period = convertDateRangeToAnalysisPeriod(await snapshot.getPromise(dateRangeInputDateRangeState(id)));
      const resolution = await snapshot.getPromise(dateRangeInputConsistencyState(id));
      return {
        id,
        label,
        period,
        resolution,
      };
    }),
  );
};

const getBenchmarkSettings = async (snapshot: Snapshot): Promise<AnalysisViewBenchmarkSettings[]> => {
  const ids = await snapshot.getPromise(benchmarkInputs);
  return Promise.all(
    ids.map(async (id) => {
      const label = await snapshot.getPromise(benchmarkInputName(id));
      const relative = await snapshot.getPromise(benchmarkInputIsRelative(id));
      const type = await snapshot.getPromise(benchmarkInputType(id));
      const subjectId = await snapshot.getPromise(benchmarkInputSubject(id));
      const subject: AnalysisViewSubject | undefined = subjectId && {
        comparisonType: 'BENCHMARK' as const,
        id:
          subjectId.privateFundId ?? subjectId.privatePortfolioId ?? subjectId.fundId ?? String(subjectId.portfolioId),
        subjectType: getAnalysisSubjectType(subjectId),
        isPrivate: !!subjectId.privateFundId || !!subjectId.privatePortfolioId,
      };
      return {
        id,
        label,
        relative,
        type,
        subject,
      };
    }),
  );
};

const filterBlockSettings = (type: CustomBlockTypeEnum, settings: CustomizedBlock) => {
  return omit<CustomizedBlock, OptionalFields>(
    settings,
    type === 'GROWTH_SIMULATION' ? [] : ['confidenceLevels', 'confidenceLevelsVisibility'],
  );
};

const getSubjects = (
  subjectGroup: SubjectWithOptionalFee[],
  benchmark: BenchmarkConfig,
  imv1Fees: Record<string, number>,
): AnalysisViewSubject[] =>
  compact([
    ...subjectGroup.map((subject, idx) => {
      const id = subject.privateFundId ?? subject.privatePortfolioId ?? subject.fundId ?? String(subject.portfolioId);
      return {
        comparisonType: idx === 0 ? ('PRIMARY' as const) : ('COMPARISON' as const),
        id,
        subjectType: getAnalysisSubjectType(subject),
        feesMapping: imv1Fees[id] ? { [id]: imv1Fees[id] } : subject.feesMapping,
        isPrivate: !!subject.privateFundId || !!subject.privatePortfolioId,
      };
    }),
    benchmark.subject && {
      comparisonType: 'BENCHMARK' as const,
      id: String(benchmark.subject.id),
      subjectType: benchmark.subject.type as AnalysisSubjectTypeEnum,
      isPrivate: benchmark.subject.private,
    },
  ]);

const getStandardLinkedFields = (globalRefId: string): LinkedItem[] => [
  {
    field: 'SUBJECTS',
    refBlockId: globalRefId,
    refField: 'SUBJECTS',
    refOutput: false,
  },
  {
    field: 'BENCHMARK',
    refBlockId: globalRefId,
    refField: 'BENCHMARK',
    refOutput: false,
  },
  {
    field: 'DATERANGE',
    refBlockId: globalRefId,
    refField: 'DATERANGE',
    refOutput: false,
  },
];

const getBenchmark = (view: AnalysisView): Subject | undefined => {
  const benchmark = view.subjects
    .filter((subject) => !subject.subjectInaccessible)
    .find((subject) => subject.comparisonType === 'BENCHMARK');
  return benchmark && analysisSubjectToSubject(benchmark);
};

const convertAnalysisPeriodToDateRange = ({ periodToDate, endDate, startDate }: AnalysisPeriod): DateRange => {
  if (!periodToDate && !endDate && !startDate) {
    return { period: 'full' };
  }
  const period = periodToDate as RangeType;
  return {
    to: endDate,
    from: startDate,
    period,
  };
};

const convertDateRangeToAnalysisPeriod = (dateRange: DateRange = {}): AnalysisPeriod => {
  return {
    endDate: dateRange.to,
    periodToDate: dateRange.period,
    startDate: dateRange.from,
  };
};

const convertSubjects = (subjects: AnalysisViewSubject[]): Subject[] =>
  subjects
    .filter((subject) => !subject.subjectInaccessible)
    .filter((subject) => subject.comparisonType !== 'BENCHMARK')
    .map(analysisSubjectToSubject);

const analysisSubjectToSubject = ({ subjectType, id, isPrivate }: AnalysisViewSubject): Subject => ({
  privateFundId: isPrivate && subjectType === 'INVESTMENT' ? id : undefined,
  privatePortfolioId: isPrivate && subjectType === 'PORTFOLIO' ? id : undefined,
  fundId: !isPrivate && subjectType === 'INVESTMENT' ? id : undefined,
  portfolioId: !isPrivate && subjectType === 'PORTFOLIO' ? Number(id) : undefined,
});

const getAnalysisSubjectType = (subject: SubjectWithOptionalFee) => {
  if (subject.fundId || subject.privateFundId) {
    return 'INVESTMENT' as const;
  }
  return 'PORTFOLIO' as const;
};

const extractFees = (view: AnalysisView): Record<string, number> => {
  const newFees = view.subjects
    .filter((subject) => !subject.subjectInaccessible)
    .reduce((current, subject) => ({ ...(subject.feesMapping ?? {}), ...current }), {});
  return {
    ...newFees,
    ...view.customViewOptions.percentageFees,
  };
};

const getInaccessibleSubjects = (view: AnalysisView) =>
  [...view.subjects, ...(view.customizedViews?.flatMap((block) => block.subjects) ?? [])]
    .filter((subject) => subject.subjectInaccessible)
    .map(analysisSubjectToSubject);
